import { useMemo, useState } from 'react';
import type { BootstrapResponse, HorizonProjectionResponse, R6ComponentXptsBetaDiagnostic } from '../lib/api';

export interface R6ComponentDiagnosticRow {
  elementId: number;
  playerName: string;
  coreXpts: number;
  componentXpts: number;
  delta: number;
  beta: R6ComponentXptsBetaDiagnostic;
}

export interface R6ScoutRow extends R6ComponentDiagnosticRow {
  teamShortName: string;
  position: string;
  price: number | null;
  positionRank: number;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function positionLabel(elementType: number | undefined): string {
  return elementType === 1 ? 'GK' : elementType === 2 ? 'DEF' : elementType === 3 ? 'MID' : elementType === 4 ? 'FWD' : '?';
}

export function buildR6ComponentDiagnosticRows(
  horizon: HorizonProjectionResponse,
  elementIds: number[],
  elements: BootstrapResponse['elements'],
): R6ComponentDiagnosticRow[] {
  const byElement = new Map(horizon.players.map((player) => [player.elementId, player]));
  const elementNames = new Map(elements.map((element) => [element.id, element.web_name || element.second_name || String(element.id)]));

  return elementIds.flatMap((elementId) => {
    const player = byElement.get(elementId);
    const gw = player?.byGameweek.find((row) => row.gameweek === horizon.startGameweek);
    const beta = gw?.componentXptsBeta;
    if (!player || !gw || !beta) return [];
    const coreXpts = gw.xPts;
    const componentXpts = beta.totalExpectedPoints;
    return [{
      elementId,
      playerName: elementNames.get(elementId) ?? player.webName ?? String(elementId),
      coreXpts,
      componentXpts,
      delta: round2(componentXpts - coreXpts),
      beta,
    }];
  });
}

export function buildR6ScoutRows(
  horizon: HorizonProjectionResponse,
  elements: BootstrapResponse['elements'],
  teams: BootstrapResponse['teams'],
): R6ScoutRow[] {
  const elementsById = new Map(elements.map((element) => [element.id, element]));
  const teamNames = new Map(teams.map((team) => [team.id, team.short_name]));
  const raw = horizon.players.flatMap((player) => {
    const element = elementsById.get(player.elementId);
    const gw = player.byGameweek.find((row) => row.gameweek === horizon.startGameweek);
    const beta = gw?.componentXptsBeta;
    if (!element || !gw || !beta) return [];
    const componentXpts = beta.totalExpectedPoints;
    return [{
      elementId: player.elementId,
      playerName: element.web_name || element.second_name || player.webName || String(player.elementId),
      teamShortName: teamNames.get(element.team) ?? String(element.team),
      position: positionLabel(element.element_type),
      price: typeof element.now_cost === 'number' ? round2(element.now_cost / 10) : player.currentPrice,
      coreXpts: gw.xPts,
      componentXpts,
      delta: round2(componentXpts - gw.xPts),
      beta,
      positionRank: 0,
    } satisfies R6ScoutRow];
  }).sort((a, b) => b.componentXpts - a.componentXpts || b.coreXpts - a.coreXpts || a.elementId - b.elementId);

  const rankByPosition = new Map<string, number>();
  return raw.map((row) => {
    const rank = (rankByPosition.get(row.position) ?? 0) + 1;
    rankByPosition.set(row.position, rank);
    return { ...row, positionRank: rank };
  });
}

function signed(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}`;
}

function compactRefs(beta: R6ComponentXptsBetaDiagnostic): string {
  const refs = [...new Set([
    ...beta.components.flatMap((component) => component.sourceRefs),
    ...beta.inputAudit.minutes.sources,
    ...beta.inputAudit.dc.sources,
    ...beta.inputAudit.roleInputs.flatMap((row) => row.provenanceRefs),
  ].filter(Boolean))];
  if (refs.length === 0) return '—';
  return refs.length <= 4 ? refs.join(' · ') : `${refs.slice(0, 4).join(' · ')} · +${refs.length - 4} more`;
}

function coverage(beta: R6ComponentXptsBetaDiagnostic): string {
  const valued = new Set(['VALUED', 'VALUED_PARTIAL_INPUT', 'VALUED_AGGREGATE_BASELINE', 'NOT_APPLICABLE']);
  const pending = [...new Set(beta.components
    .map((component) => component.status)
    .filter((status) => !valued.has(status)))];
  return pending.length > 0 ? `${beta.status} · ${pending.join(' / ')}` : beta.status;
}

function componentBreakdown(beta: R6ComponentXptsBetaDiagnostic): string {
  return beta.components.map((component) => {
    const displayKey = component.status === 'VALUED_AGGREGATE_BASELINE' && component.key === 'OPEN_PLAY_GOALS'
      ? 'ATTACKING_GOALS_BASELINE'
      : component.status === 'VALUED_AGGREGATE_BASELINE' && component.key === 'OPEN_PLAY_ASSISTS'
        ? 'ATTACKING_ASSISTS_BASELINE'
        : component.key;
    return `${displayKey} ${component.expectedPoints.toFixed(2)} [${component.status}]`;
  }).join(' · ');
}

function R6ScoutBoard({ horizon, elements, teams }: {
  horizon: HorizonProjectionResponse;
  elements: BootstrapResponse['elements'];
  teams: BootstrapResponse['teams'];
}) {
  const [query, setQuery] = useState('');
  const rows = useMemo(() => buildR6ScoutRows(horizon, elements, teams), [horizon, elements, teams]);
  const normalized = query.trim().toLowerCase();
  const visible = normalized
    ? rows.filter((row) => `${row.playerName} ${row.teamShortName} ${row.position} ${row.elementId}`.toLowerCase().includes(normalized)).slice(0, 60)
    : rows.slice(0, 32);

  return (
    <details className="mt-3 rounded-lg border border-fuchsia-900/40 bg-slate-950/30 p-2" data-r6-all-player-scan="true">
      <summary className="cursor-pointer text-[10px] font-black uppercase tracking-wide text-fuchsia-200">
        R6.1 PLAYER SCOUT · ALL-PLAYER SCAN · {rows.length} PLAYERS · TOP 32 DEFAULT
      </summary>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search any FPL player — e.g. surname, club, ID"
          className="min-w-[320px] flex-1 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white outline-none focus:border-fuchsia-500"
          data-r6-player-search="true"
        />
        <span className="text-[10px] text-slate-500">{normalized ? `${visible.length} match(es)` : 'ranked by current-GW component xPts'}</span>
      </div>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full min-w-[1050px] text-left text-[10px]">
          <thead className="uppercase text-slate-600"><tr><th>R6 rank</th><th>Player</th><th>Pos</th><th>Club</th><th>£</th><th>Legacy</th><th>R6.1 beta</th><th>Δ</th><th>Coverage</th><th>Key components</th></tr></thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.elementId} className="border-t border-slate-800 align-top">
                <td className="py-2 pr-3">#{row.positionRank} {row.position}</td>
                <td className="pr-3 font-bold text-slate-200">{row.playerName} <span className="text-slate-600">#{row.elementId}</span></td>
                <td className="pr-3">{row.position}</td>
                <td className="pr-3">{row.teamShortName}</td>
                <td className="pr-3">{row.price === null ? '—' : row.price.toFixed(1)}</td>
                <td className="pr-3">{row.coreXpts.toFixed(2)}</td>
                <td className="pr-3 font-black text-fuchsia-200">{row.componentXpts.toFixed(2)}</td>
                <td className="pr-3">{signed(row.delta)}</td>
                <td className="max-w-[240px] pr-3 text-slate-500">{coverage(row.beta)}</td>
                <td className="max-w-[420px] text-slate-500">{componentBreakdown(row.beta)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[10px] text-slate-600">This scout scans every FPL element using the same R6.1 sidecar arithmetic. Search is diagnostic only; it cannot add a player to a route or change captaincy.</p>
    </details>
  );
}

export function R6ComponentDiagnostic({
  horizon,
  elementIds,
  elements,
  teams,
  routeLabel,
}: {
  horizon: HorizonProjectionResponse;
  elementIds: number[];
  elements: BootstrapResponse['elements'];
  teams: BootstrapResponse['teams'];
  routeLabel: string;
}) {
  const rows = buildR6ComponentDiagnosticRows(horizon, elementIds, elements);
  if (rows.length === 0) return null;

  return (
    <div className="rounded-xl border border-fuchsia-800/40 bg-fuchsia-950/10 p-3" data-r6-component-xpts="diagnostic-only">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="eyebrow">R6 COMPONENT XPTS · BETA · DIAGNOSTIC ONLY</div>
          <div className="mt-1 text-[10px] font-black uppercase tracking-wide text-fuchsia-200">R6.1 CAUSAL FOOTBALL BASELINE · COMPONENTXPTSBETA.V2</div>
          <div className="mt-1 text-[10px] font-black uppercase tracking-wide text-amber-300">NOT DECISION-GRADE</div>
          <div className="mt-1 text-[10px] font-black uppercase tracking-wide text-cyan-200">DOES NOT AFFECT CORE</div>
          <div className="mt-1 text-[10px] font-black uppercase tracking-wide text-cyan-200">DOES NOT AFFECT ROUTES / CAPTAINCY / OPTIMIZER</div>
        </div>
        <strong className="text-xs text-fuchsia-200">{routeLabel}</strong>
      </div>

      <div className="mt-2 rounded-lg border border-fuchsia-900/40 bg-fuchsia-950/20 px-3 py-2 text-[10px] leading-5 text-slate-400">
        R6.1 now values minutes, a shrunk aggregate attacking baseline exactly once, fixture/team goal environment, clean sheets, expected goals-conceded deductions, DC, keeper saves and negative events. Aggregate xG/xA is <strong className="text-fuchsia-200">not called open-play truth</strong>; separate penalty/corner/FK/long-throw uplifts stay zero until event-separated inputs exist. Bonus remains audit-only pending a full-match 2026/27 BPS model.
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[1500px] text-left text-[10px]">
          <thead className="uppercase text-slate-600">
            <tr>
              <th>Player / ID</th>
              <th>Legacy CORE xPts</th>
              <th>Component xPts BETA</th>
              <th>Δ display only</th>
              <th>Coverage / pending</th>
              <th>Confidence</th>
              <th>Component breakdown</th>
              <th>Provenance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.elementId} className="border-t border-slate-800 align-top">
                <td className="py-2 pr-3 font-bold text-slate-200">{row.playerName} <span className="text-slate-600">#{row.elementId}</span></td>
                <td className="pr-3">{row.coreXpts.toFixed(2)}</td>
                <td className="pr-3 font-black text-fuchsia-200">{row.componentXpts.toFixed(2)}</td>
                <td className="pr-3" data-r6-display-only="true">{signed(row.delta)}</td>
                <td className="max-w-[250px] pr-3 text-slate-400">{coverage(row.beta)}</td>
                <td className="pr-3">MIN {row.beta.inputAudit.minutes.confidence} · DC {row.beta.inputAudit.dc.confidence}</td>
                <td className="max-w-[520px] pr-3 text-slate-400">{componentBreakdown(row.beta)}</td>
                <td className="max-w-[420px] break-words text-slate-500">{compactRefs(row.beta)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[10px] text-slate-600">Component totals are the partial R6 BETA arithmetic sum only. Deferred channels remain PENDING_NOT_VALUED; missing inputs stay explicit and no missing value is invented.</div>
      <R6ScoutBoard horizon={horizon} elements={elements} teams={teams} />
    </div>
  );
}
